import { defineStore, acceptHMRUpdate } from 'pinia';
import { storeConfig } from '@/shared/storeConfig';

/**
 * @module GamesWinnersStore
 * @description Manages game win statistics data retrieved from the API.
 *
 * This store is responsible for tracking and providing game win statistics across the application.
 * It maintains two primary datasets:
 * - Top wins: Highest value wins across all games
 * - Latest wins: Most recent wins in chronological order
 *
 * Each dataset has its own loading state and error handling, making it easy to track
 * the state of API requests and display appropriate UI feedback.
 *
 * @remarks
 * The store is designed to be initialized once and accessed throughout the application.
 * It doesn't automatically fetch data - appropriate actions must be called to populate the store.
 *
 * @returns {Object} The store instance with reactive state and methods
 *
 * @property {Ref<TopWin[]>} `topWins` - Reactive ref containing sorted list of highest win records
 * @property {Ref<boolean>} `isLoadingTopWins` - Reactive ref tracking the state of top wins data fetching
 * @property {Ref<string|null>} `topWinsError` - Error message for failures in the top wins API request
 * @property {Ref<LastWin[]>} `lastWins` - Reactive ref containing list of most recent wins
 * @property {Ref<boolean>} `isLoadingLastWins` - Reactive ref tracking the state of latest wins data
 * @property {Ref<string|null>} `lastWinsError` - Error message for failures in the latest wins API request
 *
 * @example
 * // Import in a component
 * import { useGamesWinnersStore } from '@/stores/gamesWinners';
 * import { storeToRefs } from 'pinia';
 *
 * // Access the store and extract reactive properties
 * const gamesWinnersStore = useGamesWinnersStore();
 * const { topWins, lastWins, isLoadingTopWins, isLoadingLastWins } = storeToRefs(gamesWinnersStore);
 *
 * // Use the data in your component
 * const hasWinners = computed(() => topWins.value.length > 0);
 *
 * // Handle loading state
 * const isLoading = computed(() => isLoadingTopWins.value || isLoadingLastWins.value);
 */
const gamesWinnersStore = () => {
  // Estados
  const topWins = ref<TopWin[]>([]);
  const isLoadingTopWins = ref<boolean>(false);
  const topWinsError = ref<string | null>(null);

  const lastWins = ref<LastWin[]>([]);
  const isLoadingLastWins = ref<boolean>(false);
  const lastWinsError = ref<string | null>(null);

  /**
   * Resets all store state to initial values
   *
   * @description
   * This method clears all wins data, resets loading states and clears any error messages.
   * Useful in several scenarios:
   * - When needing to clear the store state between user sessions
   * - Before re-initialization of the store
   * - When implementing a refresh mechanism
   * - During cleanup in component unmount hooks
   *
   * @returns {void}
   */
  function $reset() {
    topWins.value = [];
    isLoadingTopWins.value = false;
    topWinsError.value = null;

    lastWins.value = [];
    isLoadingLastWins.value = false;
    lastWinsError.value = null;
  }

  return {
    topWins,
    isLoadingTopWins,
    topWinsError,
    lastWins,
    isLoadingLastWins,
    lastWinsError,
    $reset,
  };
};

export const useGamesWinnersStore = defineStore(
  'gamesWinners',
  gamesWinnersStore,
  storeConfig,
);

// HMR
// @ts-ignore
if (import.meta.hot) {
  // @ts-ignore
  import.meta.hot.accept(
    // @ts-ignore
    acceptHMRUpdate(useGamesWinnersStore, import.meta.hot),
  );
}
